import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import AppAnalytics from '../analytics/AppAnalytics';

@customElement('email-modal')
export class EmailModal extends LitElement {
  @property({ type: Boolean, reflect: true })
  open = false;

  @property({ type: String })
  source = '';

  @state()
  private email = '';

  @state()
  private isSubmitting = false;

  @state()
  private isSubmitted = false;

  @state()
  private error = '';

  static styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: 100;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease-out;
    }

    :host([open]) {
      opacity: 1;
      pointer-events: auto;
    }

    .backdrop {
      position: absolute;
      inset: 0;
      background: rgba(5, 10, 20, 0.9);
      backdrop-filter: blur(4px);
    }

    .modal {
      position: relative;
      background: linear-gradient(135deg,
        rgba(0, 212, 255, 0.1) 0%,
        rgba(168, 85, 247, 0.1) 50%,
        rgba(236, 72, 153, 0.1) 100%);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(0, 212, 255, 0.3);
      border-radius: 1rem;
      padding: 2rem;
      max-width: 28rem;
      margin: 1rem;
      width: 100%;
      transform: scale(0.9);
      transition: transform 0.2s ease-out;
    }

    :host([open]) .modal {
      transform: scale(1);
    }

    .close-button {
      position: absolute;
      z-index: 1;
      top: 1rem;
      right: 1rem;
      color: rgba(226, 232, 240, 0.6);
      background: none;
      border: none;
      cursor: pointer;
      transition: color 0.2s;
      padding: 0.5rem;
    }

    .close-button:hover {
      color: #00d4ff;
    }

    .modal-header {
      text-align: center;
      margin-bottom: 2rem;
    }

    .emoji {
      font-size: 3rem;
      margin-bottom: 1rem;
      display: block;
      animation: float 6s ease-in-out infinite;
    }

    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }

    .title {
      font-size: 1.5rem;
      font-weight: bold;
      font-family: 'JetBrains Mono', monospace;
      color: #00d4ff;
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
      margin-bottom: 1rem;
    }

    .description {
      color: rgba(226, 232, 240, 0.7);
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.875rem;
      line-height: 1.6;
    }

    .form {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }

    .input-group {
      display: flex;
      flex-direction: column;
    }

    .label {
      font-size: 0.875rem;
      font-family: 'JetBrains Mono', monospace;
      font-weight: 500;
      color: #00d4ff;
      margin-bottom: 0.5rem;
    }

    .input {
      width: 100%;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      border: 2px solid transparent;
      background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(168, 85, 247, 0.1)) padding-box,
                  linear-gradient(135deg, #00d4ff, #a855f7) border-box;
      background-color: rgba(26, 29, 41, 0.5);
      color: #e2e8f0;
      font-family: 'JetBrains Mono', monospace;
      transition: all 0.2s;
    }

    .input:focus {
      outline: none;
      border-color: #00d4ff;
      box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.2);
    }

    .input:disabled {
      opacity: 0.5;
    }

    .error {
      text-align: center;
      padding: 0.75rem;
      border-radius: 0.5rem;
      background: rgba(239, 68, 68, 0.1);
      border: 1px solid rgba(239, 68, 68, 0.3);
      color: #f87171;
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.875rem;
    }

    .submit-button {
      width: 100%;
      border: 2px solid transparent;
      background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(168, 85, 247, 0.1)) padding-box,
                  linear-gradient(135deg, #00d4ff, #a855f7) border-box;
      background-image: linear-gradient(to right, #0ea5e9, #00d4ff);
      color: #0a0f1b;
      padding: 0.75rem;
      border-radius: 0.5rem;
      font-family: 'JetBrains Mono', monospace;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }

    .submit-button:hover:not(:disabled) {
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
      transform: scale(1.05);
    }

    .submit-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }

    .spinner {
      width: 1.25rem;
      height: 1.25rem;
      border: 2px solid transparent;
      border-top: 2px solid currentColor;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .success {
      text-align: center;
    }

    .success-emoji {
      font-size: 4rem;
      margin-bottom: 1.5rem;
      display: block;
      animation: float 6s ease-in-out infinite;
    }

    .success-title {
      font-size: 1.5rem;
      font-weight: bold;
      font-family: 'JetBrains Mono', monospace;
      color: #00ff88;
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
      margin-bottom: 1rem;
    }

    .success-description {
      color: rgba(226, 232, 240, 0.7);
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.875rem;
      line-height: 1.6;
      margin-bottom: 1.5rem;
    }

    .success-button {
      border: 2px solid transparent;
      background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(168, 85, 247, 0.1)) padding-box,
                  linear-gradient(135deg, #00d4ff, #a855f7) border-box;
      background-image: linear-gradient(to right, #00ff88, #0ea5e9);
      color: #0a0f1b;
      padding: 0.75rem 1.5rem;
      border-radius: 0.5rem;
      font-family: 'JetBrains Mono', monospace;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.2s;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
    }

    .success-button:hover {
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
      transform: scale(1.05);
    }

    .privacy {
      margin-top: 1.5rem;
      text-align: center;
      font-size: 0.75rem;
      color: rgba(226, 232, 240, 0.5);
      font-family: 'JetBrains Mono', monospace;
    }

    .cyber-green { color: #00ff88; }
    .cyber-yellow { color: #ffd700; }
    .cyber-purple { color: #a855f7; }
  `;

  private async submitEmail() {
    if (!this.email) return;

    this.isSubmitting = true;
    this.error = '';

    // Track email capture attempt
    AppAnalytics.getPlausible()?.trackEvent('email_capture_attempt', {
      props: {
        source: this.source || 'unknown',
        email_domain: this.email.split('@')[1] || 'unknown'
      }
    });

    try {
      const response = await fetch('https://crm.difflora.com/new-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: this.email,
          siteId: 'difflora.com'
        })
      });

      if (response.ok) {
        this.isSubmitted = true;

        // Track successful email capture
        AppAnalytics.getPlausible()?.trackEvent('email_captured', {
          props: {
            source: this.source || 'unknown',
            email_domain: this.email.split('@')[1] || 'unknown'
          }
        });

        this.email = '';
      } else {
        this.error = 'Failed to submit. Please try again.';

        // Track email capture error
        AppAnalytics.getPlausible()?.trackEvent('email_capture_error', {
          props: {
            source: this.source || 'unknown',
            error_type: 'api_error'
          }
        });
      }
    } catch (err) {
      this.error = 'Network error. Please try again.';

      // Track email capture error
      AppAnalytics.getPlausible()?.trackEvent('email_capture_error', {
        props: {
          source: this.source || 'unknown',
          error_type: 'network_error'
        }
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  private closeModal() {
    this.open = false;
    this.removeAttribute('open');
    // Reset state after animation
    setTimeout(() => {
      this.reset();
    }, 200);
  }

  private reset() {
    this.email = '';
    this.isSubmitting = false;
    this.isSubmitted = false;
    this.error = '';
  }

  private handleBackdropClick(e: Event) {
    if (e.target === e.currentTarget) {
      this.closeModal();
    }
  }

  private handleSubmit(e: Event) {
    e.preventDefault();
    this.submitEmail();
  }

  private handleEmailInput(e: Event) {
    const target = e.target as HTMLInputElement;
    this.email = target.value;
    this.requestUpdate()
  }

  render() {
    if (!this.open) {
      return html``;
    }

    if (this.isSubmitted) {
      return html`
        <div class="backdrop" @click=${this.handleBackdropClick}></div>
        <div class="modal">
          <button class="close-button" @click=${this.closeModal}>
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>

          <div class="success">
            <span class="success-emoji">✅</span>
            <h2 class="success-title">
              <span class="cyber-yellow">[</span>SUCCESS<span class="cyber-yellow">]</span>
            </h2>
            <p class="success-description">
              <span class="cyber-green">[CONFIRMED]</span> You've been added to our launch list! We'll notify you as soon as Difflora goes live.
            </p>
            <button class="success-button" @click=${this.closeModal}>
              <span>🎉</span>AWESOME
            </button>
          </div>
        </div>
      `;
    }

    return html`
      <div class="backdrop" @click=${this.handleBackdropClick}></div>
      <div class="modal">
        <button class="close-button" @click=${this.closeModal}>
          <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>

        <div class="modal-header">
          <span class="emoji">🚀</span>
          <h2 class="title">
            <span class="cyber-green">[</span>LAUNCHING_SOON<span class="cyber-green">]</span>
          </h2>
          <p class="description">
            <span class="cyber-yellow">[STATUS]</span> Difflora is currently in development. Be the first to know when we launch!
          </p>
        </div>

        <form class="form" @submit=${this.handleSubmit}>
          <div class="input-group">
            <label class="label">
              <span class="cyber-purple">[</span>EMAIL_ADDRESS<span class="cyber-purple">]</span>
            </label>
            <input
              type="email"
              class="input"
              .value=${this.email}
              @input=${this.handleEmailInput}
              ?disabled=${this.isSubmitting}
              placeholder="ENTER_YOUR_EMAIL"
              required>
          </div>

          ${this.error ? html`
            <div class="error">
              [ERROR] ${this.error}
            </div>
          ` : ''}

          <button
            type="submit"
            class="submit-button"
            ?disabled=${this.isSubmitting || !this.email}>
            ${this.isSubmitting ? html`
              <span class="spinner"></span>
              SUBMITTING...
            ` : html`
              <span>📧</span>NOTIFY_ME_ON_LAUNCH
            `}
          </button>
        </form>

        <div class="privacy">
          <span class="cyber-green">[PRIVACY]</span> We'll only email you about our launch. No spam, ever.
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'email-modal': EmailModal;
  }
}
